import { parseAddressComponents } from "@liveswitch-contact/types";

import { logError, logInfo } from "@/helpers/logError";

export { parseAddressComponents };

const EARTH_RADIUS_KM = 6371; // Earth's radius in kilometres
const KM_PER_MILE = 1.60934;
const FEET_PER_MILE = 5280;
const METRES_PER_KM = 1000;

export type MeasurementSystem = "imperial" | "metric";

// LSP-16755: distances are formatted per measurement system, not per country. Only the
// US (plus a couple of others not yet in our customer base) uses imperial, so the
// country→system mapping lives here as the single source of truth — extend the imperial
// set if those localities ever need imperial. Unknown country falls back to metric: the
// reported bug was non-US orgs seeing feet/miles, so metric is the safe default.
const IMPERIAL_COUNTRY_CODES = new Set(["US"]);

export function measurementSystemForCountry(countryCode?: string): MeasurementSystem {
	return countryCode && IMPERIAL_COUNTRY_CODES.has(countryCode.toUpperCase()) ? "imperial" : "metric";
}

// Pass the org's default country code (e.g. from
// useOrganization().currentOrganizationDefaultCountry) so the distance string follows
// that locality's measurement system.
export function calculateDistance(from: UserLocation, to: UserLocation, countryCode?: string): string {
	const dLat = toRadians(to.latitude - from.latitude);
	const dLng = toRadians(to.longitude - from.longitude);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const distanceKm = EARTH_RADIUS_KM * c;

	if (measurementSystemForCountry(countryCode) === "imperial") {
		const distanceMiles = distanceKm / KM_PER_MILE;
		if (distanceMiles < 0.1) {
			return `${Math.round(distanceMiles * FEET_PER_MILE)} ft`;
		}
		return `${distanceMiles.toFixed(1)} mi`;
	}

	if (distanceKm < 0.1) {
		return `${Math.round(distanceKm * METRES_PER_KM)} m`;
	}
	return `${distanceKm.toFixed(1)} km`;
}

function toRadians(degrees: number): number {
	return degrees * (Math.PI / 180);
}

// W3C GeolocationPositionError codes (1/2/3) are fixed across all browsers and instances,
// so the name + user-facing message pair for each lives at module scope as a single source
// of truth — avoids rebuilding twin lookup maps on every error callback and prevents the two
// from drifting out of key-alignment.
const GEOLOCATION_ERROR_DETAILS: Record<number, { name: string; message: string }> = {
	1: { name: "PERMISSION_DENIED", message: "Location access denied. Please enable location permissions and try again." },
	2: { name: "POSITION_UNAVAILABLE", message: "Location information unavailable. Please try again." },
	3: { name: "TIMEOUT", message: "We could not get your location. Please check that location services are turned on, then try again." }
};
const UNKNOWN_GEOLOCATION_ERROR = { name: "UNKNOWN", message: "Unable to get your location. Please try again." };

export async function getCurrentLocation(
	options: Partial<PositionOptions> = {},
	diagnosticsContext: Record<string, unknown> = {}
): Promise<UserLocation> {
	const startTime = performance.now();
	const defaultPositionOptions: PositionOptions = {
		enableHighAccuracy: true,
		timeout: 15000,
		maximumAge: 0
	};
	const finalPositionOptions: PositionOptions = { ...defaultPositionOptions, ...options };

	// `feature` defaults to the neutral helper name so callers that pass no context (e.g.
	// AddressLookup) are not mislabelled; the spread lets each caller override it with its
	// own feature tag (GpsLocationFilter sends "gps-location-filter").
	const buildDiagnosticsContext = (errorContext: Record<string, unknown> = {}) => ({
		feature: "getCurrentLocation",
		protocol: location.protocol,
		userAgent: navigator.userAgent,
		...diagnosticsContext,
		elapsedMs: performance.now() - startTime,
		enableHighAccuracy: finalPositionOptions.enableHighAccuracy,
		timeoutMs: finalPositionOptions.timeout,
		maximumAge: finalPositionOptions.maximumAge,
		...errorContext
	});

	if (!navigator.geolocation) {
		const error = new Error("Geolocation is not supported by this browser.");
		logError("getCurrentLocation: geolocation is not supported", error, buildDiagnosticsContext());
		throw error;
	}

	if (location.protocol !== "https:" && location.hostname !== "localhost") {
		const error = new Error("Location access requires HTTPS. Please use a secure connection.");
		logError("getCurrentLocation: geolocation requires HTTPS", error, buildDiagnosticsContext());
		throw error;
	}

	return new Promise<UserLocation>((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				logInfo("getCurrentLocation: current location acquired", buildDiagnosticsContext());
				resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude
				});
			},
			(error) => {
				const errorDetails = GEOLOCATION_ERROR_DETAILS[error.code] ?? UNKNOWN_GEOLOCATION_ERROR;
				const context = buildDiagnosticsContext({
					errorCode: error.code,
					errorName: errorDetails.name
				});

				if (error.code === error.PERMISSION_DENIED) {
					logInfo("getCurrentLocation: geolocation permission denied", context);
				} else {
					logError("getCurrentLocation: geolocation request failed", error, context);
				}
				reject(new Error(errorDetails.message));
			},
			finalPositionOptions
		);
	});
}

// Extracts a 2-letter CLDR region code from the browser's locale, e.g. "en-US" → "US",
// "zh-Hans-CN" → "CN". Used to geographically bias Google Places autocomplete without
// prompting the user for geolocation permission. Uses Intl.Locale to parse BCP 47
// semantically — positional parsing would mis-treat Unicode extension keys like
// "en-u-co-phonebk" (collation) as the region. Returns undefined for UN M.49 region
// codes like "es-419" since Google's `region` expects a 2-letter CLDR code.
export function getRegionFromBrowserLocale(): string | undefined {
	const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
	for (const tag of candidates) {
		if (!tag) continue;
		try {
			const region = new Intl.Locale(tag).region;
			if (region && /^[A-Za-z]{2}$/.test(region)) {
				return region.toUpperCase();
			}
		} catch {
			// Invalid BCP 47 tag — try the next candidate.
		}
	}
	return undefined;
}

/**
 * Returns a function that stops the poll. Callers must call it when they unmount: otherwise the poll keeps running
 * for up to 5 s after the component (or a test's DOM) is gone.
 */
export function clearAutocompletePlaceholder(): () => void {
	// due to what seems to be a timing bug? in this vuetify google autocomplete, we need to clear the placeholder manually
	// https://github.com/MadimetjaShika/vuetify-google-autocomplete/issues/118
	// the proposed solution didn't work but this did

	let attempts = 0;
	const interval = setInterval(() => {
		attempts++;
		const input = document.querySelector("input.pac-target-input");
		if (input) {
			input.removeAttribute("placeholder");
			console.log(`Cleared placeholder after ${attempts} attempts.`);
			clearInterval(interval);
		} else {
			if (attempts >= 50) {
				console.log(`Could not clear placeholder after ${attempts}, clearing interval.`);
				clearInterval(interval);
			}
		}
	}, 100);
	return () => clearInterval(interval);
}

export interface UserLocation {
	latitude: number;
	longitude: number;
}

export interface GoogleMapsPlace {
	place_id: string;
	formatted_address: string;
	geometry: google.maps.places.PlaceGeometry;
	name: string;
	rating?: number | null;
	distance?: string;
	types?: string[];
	source?: string;
}

export interface Project {
	id: string;
	name: string;
	GeoAddress: GeoAddress;
}

export interface GeoAddress {
	distance: string;
	formattedAddress: string;
	latitude: number;
	longitude: number;
	placeId: string;
	streetNumber?: string | null;
	street?: string | null;
	city?: string | null;
	state?: string | null;
	postalCode?: string | null;
	country?: string | null;
}

export const MANUAL_PLACE_ID_PREFIX = "manual_";

export function isManualAddress(geoAddress: GeoAddress | null): boolean {
	return geoAddress?.placeId?.startsWith(MANUAL_PLACE_ID_PREFIX) ?? false;
}
